import { Controller } from "@hotwired/stimulus"

const RESULT_LIMIT = 24

// One compact, searchable picker for every read-only link in the share modal.
// The people payload exists once on the modal; result rows are created only
// while a picker is open, so a 1,500-person tree no longer produces thousands
// of hidden <option> nodes before the dialog can paint.
export default class extends Controller {
    static targets = ["data", "input", "id", "results", "clear"]

    connect() {
        this.people = this._readPeople()
        this._activeInput = null
        this._activeIndex = -1
        this._items = []
        this._onDocumentClick = (event) => {
            if (!this.element.contains(event.target)) this._close()
        }
        document.addEventListener("click", this._onDocumentClick)
        this.inputTargets.forEach((input) => this._syncClear(input))
    }

    disconnect() {
        document.removeEventListener("click", this._onDocumentClick)
    }

    open(event) {
        if (this._activeInput && this._activeInput !== event.currentTarget) this._close()
        this._activeInput = event.currentTarget
        let selected = this._activeInput.dataset.selectedLabel || ""
        if (selected) this._activeInput.select()
        this._render(selected ? "" : this._activeInput.value)
    }

    query(event) {
        let input = event.currentTarget
        if (this._activeInput && this._activeInput !== input) this._close()
        this._activeInput = input
        let selected = input.dataset.selectedLabel || ""
        if (input.value !== selected) input.setCustomValidity("Choose a person from the list, or use Anyone with the link.")
        this._render(input.value)
    }

    keydown(event) {
        if (event.key === "ArrowDown") {
            event.preventDefault()
            this._move(1)
        } else if (event.key === "ArrowUp") {
            event.preventDefault()
            this._move(-1)
        } else if (event.key === "Enter" && this._items.length) {
            event.preventDefault()
            this._pick(this._activeIndex < 0 ? 0 : this._activeIndex)
        } else if (event.key === "Escape") {
            this._close()
        }
    }

    blur() {
        window.setTimeout(() => this._close(), 120)
    }

    clear(event) {
        event.preventDefault()
        let picker = event.currentTarget.closest(".family-tree-share-picker")
        let input = picker?.querySelector("[data-family-tree-share-picker-target='input']")
        let id = picker?.querySelector("[data-family-tree-share-picker-target='id']")
        if (!input || !id) return

        id.value = ""
        input.value = ""
        input.dataset.selectedLabel = ""
        input.setCustomValidity("")
        this._syncClear(input)
        this._close()
        input.focus()
    }

    _readPeople() {
        try {
            return JSON.parse(this.dataTarget.textContent || "[]")
        } catch (_error) {
            return []
        }
    }

    _render(raw) {
        let input = this._activeInput
        if (!input) return
        let results = this._resultsFor(input)
        if (!results) return

        let query = this._normalize(raw)
        let matches = this.people.filter((person) => !query || this._normalize(`${person.name} ${person.meta || ""}`).includes(query)).slice(0, RESULT_LIMIT)
        results.replaceChildren()
        this._items = []
        this._activeIndex = -1

        matches.forEach((person, index) => {
            let row = document.createElement("button")
            row.type = "button"
            row.className = "contact-picker-item"
            row.setAttribute("role", "option")
            row.id = `${results.id}-option-${person.id}`
            row.dataset.personId = String(person.id)
            row.dataset.personLabel = person.label

            let name = document.createElement("span")
            name.className = "contact-picker-item-name"
            name.textContent = person.name
            row.appendChild(name)
            if (person.meta) {
                let meta = document.createElement("span")
                meta.className = "contact-picker-item-meta"
                meta.textContent = person.meta
                row.appendChild(meta)
            }

            row.addEventListener("mousedown", (event) => event.preventDefault())
            row.addEventListener("click", () => this._pick(index))
            row.addEventListener("mouseenter", () => this._select(index))
            results.appendChild(row)
            this._items.push(row)
        })

        if (!matches.length) {
            let empty = document.createElement("p")
            empty.className = "family-tree-share-picker-empty"
            empty.textContent = "No one by that name."
            results.appendChild(empty)
        }
        results.hidden = false
        input.setAttribute("aria-expanded", "true")
    }

    _pick(index) {
        let row = this._items[index]
        let input = this._activeInput
        let picker = input?.closest(".family-tree-share-picker")
        let id = picker?.querySelector("[data-family-tree-share-picker-target='id']")
        if (!row || !input || !id) return

        id.value = row.dataset.personId
        input.value = row.dataset.personLabel
        input.dataset.selectedLabel = row.dataset.personLabel
        input.setCustomValidity("")
        this._syncClear(input)
        this._close()
    }

    _move(delta) {
        if (!this._items.length) return
        let next = (this._activeIndex + delta + this._items.length) % this._items.length
        this._select(next)
        this._items[next].scrollIntoView({ block: "nearest" })
    }

    _select(index) {
        this._items.forEach((item, itemIndex) => {
            let active = itemIndex === index
            item.classList.toggle("is-active", active)
            item.setAttribute("aria-selected", String(active))
        })
        this._activeIndex = index
        if (this._activeInput && this._items[index]) this._activeInput.setAttribute("aria-activedescendant", this._items[index].id)
    }

    _close() {
        if (!this._activeInput) return
        let input = this._activeInput
        let results = this._resultsFor(input)
        if (results) {
            results.hidden = true
            results.replaceChildren()
        }
        input.setAttribute("aria-expanded", "false")
        input.removeAttribute("aria-activedescendant")
        this._activeInput = null
        this._items = []
        this._activeIndex = -1
    }

    _resultsFor(input) {
        return input?.closest(".family-tree-share-picker")?.querySelector("[data-family-tree-share-picker-target='results']")
    }

    _syncClear(input) {
        let picker = input.closest(".family-tree-share-picker")
        let id = picker?.querySelector("[data-family-tree-share-picker-target='id']")
        let clear = picker?.querySelector("[data-family-tree-share-picker-target='clear']")
        if (clear) clear.hidden = !id?.value
    }

    _normalize(value) {
        return String(value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim()
    }
}
